// Import Packages
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cloneDeep from "lodash/cloneDeep";
import { useSetRecoilState } from 'recoil';

// Import Material UI
import { Dialog, } from "@material-ui/core/";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import InfoIcon from "@material-ui/icons/Info";
import Tooltip from "@material-ui/core/Tooltip";
import Checkbox from "@material-ui/core/Checkbox";
import AddIcon from "@material-ui/icons/Add";
import { Autocomplete } from "@material-ui/lab";

// Import Recoil
import { alertState } from "../../../../recoil-store/recoil-state";

// Import Pages
import AppCredentialModal from "../../appCredential/appCredentialModal/AppCredentialModal";
import VariablePicker from "../../../../components/FlowConfigurationInputs/VariablePicker/VariablePicker";
import { TextInput } from "../../../../components/FlowConfigurationInputs/TextInput/NewTextInput";
import { CredentialPicker } from "../../../../components/FlowConfigurationInputs/CredentialPicker/CredentialPicker";

// Import Services
import triggerService from "../../../../services/triggerService/triggerService";
import ApplicationService from '../../../../services/ApplicationService/ApplicationService';

// Import CSS
import "./EmailTrigger.css";

const useStyles = makeStyles((theme) => ({
  input: {
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "initial",
      borderWidth: "1px",
    },
  },
  bottomSection: {
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  formControl: {
    backgroundColor: "white !important",
    width: "100%",
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  button: {
    border: "none",
    borderRadius: "5px",
    width: 100,
    height: 35,
    "&:focus": {
      outline: "none",
    },
  },
  submit: {
    color: "red",
    background: "transparent",
    border: "1px solid red",
    cursor: "pointer",
  },
  heading: {
    color: "#4A4A4A",
    fontSize: "12px !important",
    fontWeight: "bold",
  },
}));

const gmailAppId = "e36d6014-0389-4cb1-b781-a9686f44f9d3";
const EmailTrigger = ({
  setOpen,
  setOpenVariable,
  variables,
  automId,
  onTabChange, tab
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [openAppCredential, setOpenAppCredential] = useState(false);
  const [gmailApp, setGmailApp] = useState();
  const [gmailCredentials, setGmailCredentials] = useState([]);
  const [selectedCredential, setSelectedCredential] = useState({});
  const [tabCheckbox, setTabCheckbox] = useState({});

  const [profile, setProfile] = useState({ trigger_profile_id: undefined, trigger_rule_id: undefined });
  const [emailTrigger, setEmailTrigger] = useState({
    fields: {
      host: { value: "" },
      port: { value: "" },
      period: { value: 1 },
      protocol: { value: "IMAP" },
      periodType: { value: "minutes" },
      user: { value: "" },
      password: { value: "" },
      from: { value: "" },
      cc: { value: "" },
      outputVariable: { value: "" },
      emailBody: { value: false },
      bodyValue: { value: "" },
      subjectValue: { value: "" },
      emailSubject: { value: false },
      withoutAttachment: { value: false },
      withAttachment: { value: false },
      oauth_credential_id: { value: "" }
    },
  });
  const setAlert = useSetRecoilState(alertState);

  const profileType = "EMAIL";
  const label = "CONTENT.EMAIL-TRIGGER.";

  const handleChange = (event, fieldsName, fieldsValue) => {
    if (
      event.target.name === "emailSubject" ||
      event.target.name === "emailBody"
    ) {
      if (event.target.name === "emailSubject") {
        emailTrigger.fields.subjectValue.value = "";
      } else if (event.target.name === "emailBody") {
        emailTrigger.fields.bodyValue.value = "";
      }
      setEmailTrigger({
        fields: {
          ...emailTrigger.fields,
          [event.target.name]: { value: event.target.checked },
        },
      });
    }
    else if (event.target.name === "withoutAttachment") {
      setEmailTrigger({
        fields: {
          ...emailTrigger.fields,
          [event.target.name]: { value: event.target.checked },
          withAttachment: { value: false },
        },
      });
      setTabCheckbox({ status: event.target.checked, name: event.target.name })
    }
    else if (event.target.name === "withAttachment") {
      setEmailTrigger({
        fields: {
          ...emailTrigger.fields,
          [event.target.name]: { value: event.target.checked },
          withoutAttachment: { value: false },
        },
      });
      setTabCheckbox({ status: event.target.checked, name: event.target.name })
    }
    else if (fieldsName === "protocol") {
      setEmailTrigger({
        fields: {
          ...emailTrigger.fields,
          [fieldsName.toLowerCase()]: { value: fieldsValue },
        },
      });
    } else {
      setEmailTrigger({
        fields: {
          ...emailTrigger.fields,
          [event.target.name]: { value: event.target.value },
        },
      });
    }
  };

  const setGmailApplication = async () => {
    const response = await ApplicationService.getApplications({ id: gmailAppId });
    if (response && response.status && response.data.length) {
      setGmailApp(response.data[0]);
    }
  }

  const setAppCredentials = async (credential_id) => {
    const response = await ApplicationService.getAppCredentials({ application_id: gmailAppId });
    if (response && response.status && response.data.length) {
      if (credential_id) {
        let selectedData = response.data.find(x => x.id === credential_id);
        if (selectedData) {
          setSelectedCredential(selectedData);
        }
      }
      setGmailCredentials(response.data);
    }
  }

  useEffect(() => {
    if (automId !== undefined && automId !== null) {
      setLoading(true)
      async function fetchData() {
        let credential_id;
        const resp = await triggerService.getTriggerProfileByAutomId(automId, profileType);
        if (resp && resp.status && resp.code === 200) {
          const period = {
            period: { value: resp?.data?.trigger_rule?.period?.value },
            periodType: { value: resp?.data?.trigger_rule?.period?.type }
          }

          if (period.periodType.value === 'seconds') {
            period.periodType.value = "minutes"
          }
          setProfile({ trigger_profile_id: resp.data.id, trigger_rule_id: resp.data.trigger_rule.id })
          setEmailTrigger({ fields: { ...emailTrigger.fields, ...resp?.data?.trigger_rule?.configuration?.fields, ...period } })
          if (resp.data.trigger_rule?.configuration?.fields?.oauth_credential_id?.value) {
            credential_id = resp.data.trigger_rule?.configuration?.fields?.oauth_credential_id?.value;
            // setSelectedCredential(credential_id);
          }
          if (resp.data.trigger_rule?.configuration?.fields?.withAttachment?.value) {
            setTabCheckbox({ status: true, name: "withAttachment" })
          }
          else if (resp.data.trigger_rule?.configuration?.fields?.withoutAttachment?.value) {
            setTabCheckbox({ status: true, name: "withoutAttachment" })
          }
        }
        setGmailApplication();
        setAppCredentials(credential_id);
        setLoading(false);
      }
      fetchData();
    }
  }, [automId])

  const handleSave = async () => {
    if (emailTrigger.fields.protocol.value === "OAUTH") {
      if (!emailTrigger.fields.oauth_credential_id.value || !emailTrigger.fields.period.value || !emailTrigger.fields.outputVariable.value) {
        setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.STORE.FILL-THE-REQUIRED-VARIABLES"), severity: 'error' }))
        return
      }
    }
    if (emailTrigger.fields.protocol.value !== "OAUTH" && (!emailTrigger.fields.host.value ||
      !emailTrigger.fields.port.value ||
      !emailTrigger.fields.outputVariable.value ||
      !emailTrigger.fields.user.value ||
      !emailTrigger.fields.period.value)) {
      setAlert(alert => ({ ...alert, show: true, message: t("MESSAGES.STORE.FILL-THE-REQUIRED-VARIABLES"), severity: 'error' }))
    } else {
      const copyObj = cloneDeep(emailTrigger);
      delete copyObj.fields.period;
      delete copyObj.fields.periodType;
      if (emailTrigger?.fields?.protocol.value === "POP3") {
        delete copyObj.fields.from;
        delete copyObj.fields.cc;
        delete copyObj.fields.from;
        delete copyObj.fields.withAttachment;
        delete copyObj.fields.withoutAttachment;
        delete copyObj.fields.emailBody;
        delete copyObj.fields.emailSubject;
        delete copyObj.fields.bodyValue;
        delete copyObj.fields.subjectValue;
        delete copyObj.fields.oauth_credential_id;
      }
      const period = {
        value: emailTrigger.fields.period.value,
        type: emailTrigger.fields.periodType.value,
      };
      const newData = {
        configuration: copyObj,
        period: JSON.stringify(period),
        autom_id: automId,
        type: profileType,
        trigger_profile_id: profile.trigger_profile_id,
        trigger_rule_id: profile.trigger_rule_id
      };
      const response = await triggerService.createProfile(newData);
      if (response) {
        if (response?.status) {
          setAlert(alert => ({ ...alert, show: true, message: t(label + "SAVE-SUCCESS"), severity: 'success' }))
          setProfile({ trigger_profile_id: response.data.trigger_profile_id, trigger_rule_id: response.data.trigger_rule_id })
          setOpen(false);
        } else {
          setAlert(alert => ({ ...alert, show: true, message: response.message, severity: 'error' }))
        }
      }
    }
  };

  const cancel = () => {
    setOpenAppCredential(false)
  }

  const afterSave = (t) => {
    setAppCredentials();
    setOpenAppCredential(false);
  }

  const handleChangeCredential = (credential) => {
    setSelectedCredential(credential)
    emailTrigger.fields.oauth_credential_id.value = credential.id;
  }
  const handleStopScroll = () => {
    const element = document.querySelector(".email-trigger-scroll")
    element.style.overflowY = "hidden"
  };

  const handleControlScroll = () => {
    const element = document.querySelector(".email-trigger-scroll")
    element.style.overflowY = "auto"
  };

  return !loading && (

    <Dialog
      className="email-trigger-main user-detail-dialog"
      id="autom-addNew-modal"
      data-test="autom-add-new"
      open={true}
      onClose={setOpen}
      PaperProps={{
        style: {
          maxHeight: 800,
          minWidth: 600,
        },
      }}
    >
      <div className="db-tabs db-page">
        <button className={tab === 0 ? 'db-tabactive' : ''} value="license" onClick={() => onTabChange("email")}>
          {t(label + "EMAIL-CONFIGURATION")}
        </button>
        {/* <button className={tab === 1 ? 'db-tabactive' : ''} value="about" onClick={() => onTabChange("webhook")}>
          {t("CONTENT.WEBHOOK.ADD-RULE")}
        </button> */}
      </div>
      <DialogContent
        className="email-trigger-scroll"
        style={{ padding: "8px 8px 8px 24px" }}
      >
        <div>
          <div className="form-element">
            <div className="email-trigger-title">
              {t(label + "PROTOCOL-MESSAGE")}
            </div>
            <div className="w-195 mail-server-button-group" style={{ width: "195px" }}>
              <div
                name="protocol"
                onClick={(e) => handleChange(e, "protocol", "IMAP")}
                value="IMAP"
                className={
                  emailTrigger?.fields?.protocol?.value === "IMAP"
                    ? "mail-server-button-active"
                    : "mail-server-button"
                }
              >
                IMAP
              </div>
              <div
                name="protocol"
                onClick={(e) => handleChange(e, "protocol", "POP3")}
                value="POP3"
                className={
                  emailTrigger?.fields?.protocol?.value === "POP3"
                    ? "mail-server-button-active"
                    : "mail-server-button"
                }
              >
                POP3
              </div>
              <div
                name="protocol"
                onClick={(e) => handleChange(e, "protocol", "OAUTH")}
                value="OAUTH"
                className={
                  emailTrigger?.fields?.protocol?.value === "OAUTH"
                    ? "mail-server-button-active"
                    : "mail-server-button"
                }
              >
                OAuth
              </div>
            </div>
          </div>
          {
            emailTrigger.fields.protocol.value === "OAUTH" ?
              <div >
                <div style={{ width: "90%" }}>
                  <div className="dialog-text">
                    {t("CONTENT.CONFIGURATION-FORM.CUSTOM-ACTION.CREDENTIAL")}
                    <span className='required-span'>({t('CONTENT.FLOW.REQUIRED')})</span>
                    <AddIcon className="credential-add-icon" onClick={() => setOpenAppCredential(true)} />
                  </div>
                  <small className="credential-helper">{t("CONTENT.CONFIGURATION-FORM.CUSTOM-ACTION.CREDENTIAL-HELP")}</small>
                  <FormControl
                    variant="outlined"
                    style={{ width: "100%" }}
                  >
                    <Autocomplete
                      style={{
                        backgroundColor: "#fff",
                        height: "40px",
                        fontSize: "12px"
                      }}
                      onChange={(event, value, reason) => {
                        return handleChangeCredential(value ? value : "")
                      }}
                      value={selectedCredential}
                      id="demo-simple-select-outlined"
                      size="small"
                      options={gmailCredentials}
                      onFocus={handleStopScroll}
                      onBlur={handleControlScroll}
                      getOptionLabel={(item) => {
                        if (item.name) {
                          return item.name
                        }
                        return item
                      }}
                      getOptionSelected={(option, value) => option.id === value.id}
                      renderInput={(params) => <TextField {...params} variant="outlined" />}
                    />
                  </FormControl>
                </div>

                <div className="form-element">
                  <div className="email-trigger-title">{t(label + "PERIOD")} <span className='required-span'>({t('CONTENT.FLOW.REQUIRED')})</span></div>
                  <TextField
                    className={`${classes.input} email-trigger-input`}
                    variant="outlined"
                    type="number"
                    name="period"
                    value={emailTrigger?.fields?.period?.value}
                    onChange={(e) => {
                      if (Number(e.target.value) <= 0) {
                        e.target.value = "1";
                      }
                      handleChange(e)
                    }}
                  />
                </div>
                <div className="form-element">
                  <FormControl
                    variant="outlined"
                    className={`${classes.formControl} email-trigger-input`}
                    size="small"
                  >
                    <Select
                      style={{ height: 34, fontSize: 12, width: "100%" }}
                      labelId="demo-simple-select-outlined-label"
                      id="demo-simple-select-outlined"
                      name="periodType"
                      value={emailTrigger?.fields?.periodType?.value}
                      onChange={handleChange}
                    >
                      <MenuItem style={{ fontSize: 12 }} value="minutes">
                        {t(label + "MINUTES")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </div>
              </div> :
              <div>
                <div className="form-element email-trigger-textinput">
                  <div className="email-trigger-title">{t(label + "HOST")}<span className='required-span'>({t('CONTENT.FLOW.REQUIRED')})</span></div>
                  <TextInput
                    action={emailTrigger}
                    inputName="host"
                    variables={variables}
                    setOpenVariable={setOpenVariable}
                  />
                </div>
                <div className="form-element email-trigger-textinput">
                  <div className="email-trigger-title">{t(label + "PORT")} <span className='required-span'>({t('CONTENT.FLOW.REQUIRED')})</span></div>
                  <TextInput
                    action={emailTrigger}
                    inputName="port"
                    variables={variables}
                    setOpenVariable={setOpenVariable}
                  />
                </div>
                <div className="form-element">
                  <div className="email-trigger-title">{t(label + "PERIOD")} <span className='required-span'>({t('CONTENT.FLOW.REQUIRED')})</span></div>
                  <TextField
                    className={`${classes.input} email-trigger-input`}
                    variant="outlined"
                    type="number"
                    name="period"
                    value={emailTrigger?.fields?.period?.value}
                    onChange={(e) => {
                      if (Number(e.target.value) <= 0) {
                        e.target.value = "1";
                      }
                      handleChange(e)
                    }}
                  />
                </div>
                <div className="form-element">
                  <FormControl
                    variant="outlined"
                    className={`${classes.formControl} email-trigger-input`}
                    size="small"
                  >
                    <Select
                      style={{ height: 34, fontSize: 12, width: "100%" }}
                      labelId="demo-simple-select-outlined-label"
                      id="demo-simple-select-outlined"
                      name="periodType"
                      value={emailTrigger?.fields?.periodType?.value}
                      onChange={handleChange}
                    >
                      {/* <MenuItem style={{ fontSize: 12 }} value="seconds">
                  {t(label + "SECONDS")}
                </MenuItem> */}
                      <MenuItem style={{ fontSize: 12 }} value="minutes">
                        {t(label + "MINUTES")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div className="form-element">{t(label + "CREDENTIALS")} <span className='required-span'>({t('CONTENT.FLOW.REQUIRED')})</span></div>
                <div className="form-element">
                  <Accordion
                    style={{
                      background: "#FAFAFA",
                      width: "90.4%",
                      boxShadow: "none",
                      border: "0.5px solid #CFCFCF",
                    }}
                  >
                    <AccordionSummary
                      style={{ margin: "-6px 0" }}
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography className={classes.heading}>
                        {t(label + "USER")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div
                        style={{ width: "100%" }}
                        className="email-trigger-credentialpicker"
                      >
                        <CredentialPicker
                          action={emailTrigger}
                          inputName="user"
                          isRequired={false}
                          variables={variables}
                          setOpenVariable={setOpenVariable}
                        />
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
                <div className="form-element">
                  <Accordion
                    style={{
                      background: "#FAFAFA",
                      width: "90.4%",
                      boxShadow: "none",
                      border: "0.5px solid #CFCFCF",
                    }}
                  >
                    <AccordionSummary
                      style={{ margin: "-6px 0" }}
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panel1a-header"
                    >
                      <Typography className={classes.heading}>
                        {t(label + "PASSWORD")}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <div style={{ width: "100%" }}>
                        <CredentialPicker
                          action={emailTrigger}
                          inputName="password"
                          isRequired={false}
                          variables={variables}
                          setOpenVariable={setOpenVariable}
                          isPassword={true}
                        />
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>

              </div>

          }
          <div className="form-element output-variable-container">
            <div className="email-trigger-title">
              {t(label + "OUTPUT-VARIABLE")} <span className='required-span'>({t('CONTENT.FLOW.REQUIRED')})</span>
            </div>

            <VariablePicker
              action={emailTrigger}
              inputName="outputVariable"
              filter="array"
              setOpenVariable={setOpenVariable}
              variables={variables}
              handleControlScroll={handleControlScroll}
              handleStopScroll={handleStopScroll}
            />
            <small><em>{t('CONTENT.CONFIGURATION-FORM.VARIABLE-HELPER', { type: 'array' })}</em></small>
            <div>
              <small>
                Ex: {"{"}
                <div>
                  {" Id : '4445454'"}<br />
                  {" Subject: 'test'"}<br />
                  {" Body: 'test'"}<br />
                  {" To: '[]'"}<br />
                  {" Cc: '[]'"}<br />
                  {" Date: ''"}<br />
                  {" From: '[]'"}<br />
                  {" Attachments: [{'fileName':'text.txt','value':'mate:/email_attachments_11531/54532153/test.txt'}]"}<br />
                </div>
                {"}"}
              </small>
            </div>
          </div>
          {emailTrigger.fields.protocol.value !== "POP3" && (
            <>
              <div className="form-element">
                <div className="email-trigger-line" />
              </div>
              <div className="form-element">
                <div
                  id="about-title"
                  style={{ marginBottom: 26, color: "#162436" }}
                >
                  {t(label + "SELECT-CONDITIONS")}
                </div>
              </div>
              <div className="form-element email-trigger-textinput">
                <div className="email-trigger-title">{t(label + "FROM")}</div>
                <TextInput
                  action={emailTrigger}
                  inputName="from"
                  isRequired={false}
                  variables={variables}
                  setOpenVariable={setOpenVariable}
                />
              </div>
              <div className="form-element email-trigger-textinput">
                <div className="email-trigger-title">{t(label + "CC")}</div>
                <TextInput
                  action={emailTrigger}
                  inputName="cc"
                  isRequired={false}
                  variables={variables}
                  setOpenVariable={setOpenVariable}
                />
              </div>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      size="small"
                      name="emailSubject"
                      checked={emailTrigger?.fields?.emailSubject?.value}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <div
                      className="email-trigger-text"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: 2,
                      }}
                    >
                      {t(label + "EMAIL-SUBJECT")}
                      {/* <Tooltip title="lorem ipsum" placement="top">
                        <InfoIcon className="mail-server-info" />
                      </Tooltip> */}
                    </div>
                  }
                />
                {emailTrigger?.fields?.emailSubject?.value === true &&
                  <div
                    className="email-trigger-textinput"
                    style={{
                      pointerEvents: emailTrigger?.fields?.emailSubject.value
                        ? "auto"
                        : "none",
                    }}
                  >
                    <TextInput
                      action={emailTrigger}
                      inputName="subjectValue"
                      isRequired={false}
                      variables={variables}
                      setOpenVariable={setOpenVariable}
                    />
                  </div>
                }
              </div>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      size="small"
                      name="emailBody"
                      checked={emailTrigger?.fields?.emailBody?.value}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <div
                      className="email-trigger-text"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginTop: 2,
                      }}
                    >
                      {t(label + "EMAIL-BODY")}
                      <Tooltip title="It searches for words in the body. Cannot search between words" placement="top">
                        <InfoIcon className="mail-server-info" />
                      </Tooltip>
                    </div>
                  }
                />
                {emailTrigger?.fields?.emailBody.value === true &&
                  <div
                    className="email-trigger-textinput"
                    style={{
                      pointerEvents: emailTrigger?.fields?.emailBody.value
                        ? "auto"
                        : "none",
                    }}
                  >

                    <TextInput
                      action={emailTrigger}
                      inputName="bodyValue"
                      isRequired={false}
                      variables={variables}
                      setOpenVariable={setOpenVariable}
                    />
                  </div>
                }
              </div>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      size="small"
                      name="withAttachment"
                      checked={emailTrigger?.fields?.withAttachment?.value}
                      style={{ color: tabCheckbox.status && tabCheckbox.name !== "withAttachment" && "rgba(0, 0, 0, 0.38)" }}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <div
                      className="email-trigger-text"
                      style={{
                        color: tabCheckbox.status && tabCheckbox.name !== "withAttachment" && "rgba(0, 0, 0, 0.38)",
                        display: "flex",
                        alignItems: "center",
                        marginTop: 2,
                      }}
                    >
                      {t(label + "EMAIL-WITH-ATTACHMENT")}
                      {/* <Tooltip title="lorem ipsum" placement="top">
                        <InfoIcon className="mail-server-info" />
                      </Tooltip> */}
                    </div>
                  }
                />
                <small style={{ fontSize: "9px" }} className="conf-field-desc"><em>{t(label + "ATTACHMENT-HELPER")}</em></small>
              </div>
              <div>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="default"
                      size="small"
                      name="withoutAttachment"
                      checked={emailTrigger?.fields?.withoutAttachment?.value}
                      style={{ color: tabCheckbox.status && tabCheckbox.name !== "withoutAttachment" && "rgba(0, 0, 0, 0.38)" }}
                      onChange={handleChange}
                    />
                  }
                  label={
                    <div
                      className="email-trigger-text"
                      style={{
                        color: tabCheckbox.status && tabCheckbox.name !== "withoutAttachment" && "rgba(0, 0, 0, 0.38)",
                        display: "flex",
                        alignItems: "center",
                        marginTop: 2,
                      }}
                    >
                      {t(label + "EMAIL-WITHOUT-ATTACHMENT")}
                      {/* <Tooltip title="lorem ipsum" placement="top">
                        <InfoIcon className="mail-server-info" />
                      </Tooltip> */}
                    </div>
                  }
                />
                <small style={{ fontSize: "9px" }} className="conf-field-desc"><em>{t(label + "ATTACHMENT-HELPER")}</em></small>
              </div>
            </>
          )}
          {openAppCredential &&
            <AppCredentialModal appCredentialId={""} selectedApp={gmailApp}
              afterSave={afterSave} cancel={cancel} />}
        </div>
      </DialogContent>
      <DialogActions
        style={{
          justifyContent: "center",
          padding: "8px 24px 16px 24px",
        }}
      >
        <button
          type="button"
          data-test="createautom-button"
          onClick={handleSave}
          className={`${classes.button} ${classes.submit}`}
          style={{
            backgroundColor: "#162436",
            borderRadius: "4px",
            color: "#fff",
            border: "none",
            boxShadow: "0px 0px 8px -3px rgba(0,0,0,0.25)",
          }}
        >
          {t("CONTENT.FLOW.SAVE")}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailTrigger;
